import ajax from '../../utils/ajax';

const defaultOptions = {
  url: null,
  interval: 100,
  withCredentials: true
};

/**
 * Generic polling function to periodically poll a url
 * @param  {object} options Options to configure polling
 * @return {object}
 */
function Poller (options) {
  options = Object.assign({}, defaultOptions, options);

  // interval is required and must be greater than or equal to 100
  if (options.interval < 100) {
    throw new Error('Cannot create poller: interval must be greater than or equal to 100');
  }

  let timer;
  let pending;
  let lastModifiedTime;
  let started = false;
  let pollingURL = options.url;
  const subscribers = [];

  /**
   * Notifies subscribers when poller receives a new response
   *
   * @param {object|false} error    When polling fails an error, otherwise false
   * @param {object}       response The polling response
   */
  const notify = function (error, response) {
    subscribers.forEach(function (subscriber) {
      subscriber.callback.call(subscriber.context, error, response);
    });
  };

  /**
   * Handler ajax responses
   *
   * @param {object} response
   */
  const successHandler = function (response) {
    pending = null;
    if (response.ok) {
      // update last modified date
      lastModifiedTime = response.headers.get('Last-Modified');
      pollAgain();
      // execute the callback with the current poll response
      notify(false, response);
    } else {
      pollAgain();
      notify({
        name: 'Network',
        message: response.statusText
      }, false);
    }
  };

  /**
   * Handle ajax errors
   *
   * @param {object} error The error that occurred
   */
  const errorHandler = function (error) {
    pending = null;
    pollAgain();
    notify(error, false);
  };

  /**
   * Execute a polling operation
   */
  const poll = function () {
    if (!started || pending) {
      return;
    }

    // build the request options
    const request = {
      headers: []
    };
    // configure request
    if (options.withCredentials) {
      request.withCredentials = true;
    }
    // only return updates since the last modified date
    if (lastModifiedTime) {
      request.headers.push(`If-Modified-Since: ${lastModifiedTime}`);
    }

    try {
      // execute the request
      pending = ajax(pollingURL, request);
      pending
        .then(successHandler)
        .catch(errorHandler);
    } catch (e) {
      errorHandler(e);
    }
  };

  /**
   * Schedule the next polling operation
   */
  const pollAgain = function () {
    if (started && !pending) {
      timer = setTimeout(poll, options.interval);
    }
  };

  /**
   * Add a callback to the subscribers list
   *
   * @param {function} callback The callback to fire when new polling data is received
   */
  const subscribe = function (callback, context = null) {
    return subscribers.push({ callback, context }) - 1;
  };

  /**
   * Start polling for new data
   */
  const start = function () {
    if (started) return;

    // url is required
    if (!pollingURL) {
      throw new Error('Cannot start polling: missing url');
    }
    started = true;
    poll();
  };

  /**
   * Stop polling for data
   */
  const stop = function () {
    if (!started) return;

    started = false;
    // cancel pending xhr
    if (pending) pending.cancel();
    pending = null;
    // cancel timer
    clearTimeout(timer);
  };

  return {
    subscribe: subscribe,
    start: start,
    stop: stop,
    setUrl: function (url) {
      pollingURL = url;
      return this;
    }
  };
}

export default Poller;
